/**
 * Reversi Error Handler
 *
 * Wraps ErrorHandlingService for game-specific error classification.
 * All user-facing messages go through NotificationService — never raw alert().
 *
 * Follows SnakesLaddersErrorHandler pattern.
 */

import {
  ErrorHandlingService,
  ErrorType,
  ErrorSeverity,
} from '../../services/ErrorHandlingService';
import {
  NotificationService,
  NotificationType,
} from '../../services/NotificationService';

export enum ReversiErrorType {
  INVALID_MOVE = 'INVALID_MOVE',
  NOT_YOUR_TURN = 'NOT_YOUR_TURN',
  GAME_ENDED = 'GAME_ENDED',
  GAME_LOGIC = 'GAME_LOGIC',
  CONNECTION_ERROR = 'CONNECTION_ERROR',
  CORRUPTED_DATA = 'CORRUPTED_DATA',
  AI_TIMEOUT = 'AI_TIMEOUT',
  MULTIPLAYER_VALIDATION = 'MULTIPLAYER_VALIDATION',
  SAVE_ERROR = 'SAVE_ERROR',
  LOAD_ERROR = 'LOAD_ERROR',
  UNKNOWN = 'UNKNOWN',
}

export interface ReversiError {
  type: ReversiErrorType;
  message: string;
  details?: any;
  timestamp: number;
  recoverable: boolean;
}

export class ReversiErrorHandler {
  private errorService: ErrorHandlingService;
  private notificationService: NotificationService;
  private errorLog: ReversiError[] = [];
  private readonly MAX_ERROR_LOG = 50;

  constructor(
    errorService: ErrorHandlingService,
    notificationService: NotificationService
  ) {
    this.errorService = errorService;
    this.notificationService = notificationService;
  }

  handleError(
    type: ReversiErrorType,
    message: string,
    details?: any,
    showNotification: boolean = true
  ): ReversiError {
    const error: ReversiError = {
      type,
      message,
      details,
      timestamp: Date.now(),
      recoverable: this.isRecoverable(type),
    };

    this.logError(error);

    const { errorType, severity } = this.classifyError(type);
    this.errorService.handleError(
      new Error(message),
      errorType,
      severity,
      { component: 'Reversi', additionalData: details },
      { showNotification: false }
    );

    if (showNotification) {
      this.showErrorNotification(error);
    }

    return error;
  }

  private classifyError(
    type: ReversiErrorType
  ): { errorType: ErrorType; severity: ErrorSeverity } {
    switch (type) {
      case ReversiErrorType.INVALID_MOVE:
      case ReversiErrorType.NOT_YOUR_TURN:
      case ReversiErrorType.GAME_ENDED:
      case ReversiErrorType.GAME_LOGIC:
      case ReversiErrorType.AI_TIMEOUT:
        return { errorType: ErrorType.GAME_LOGIC, severity: ErrorSeverity.LOW };

      case ReversiErrorType.CONNECTION_ERROR:
      case ReversiErrorType.MULTIPLAYER_VALIDATION:
        return { errorType: ErrorType.MULTIPLAYER, severity: ErrorSeverity.MEDIUM };

      case ReversiErrorType.CORRUPTED_DATA:
      case ReversiErrorType.SAVE_ERROR:
      case ReversiErrorType.LOAD_ERROR:
        return { errorType: ErrorType.STORAGE, severity: ErrorSeverity.MEDIUM };

      default:
        return { errorType: ErrorType.SYSTEM, severity: ErrorSeverity.MEDIUM };
    }
  }

  // Convenience methods
  handleGameLogicError(reason: string): ReversiError {
    return this.handleError(
      ReversiErrorType.GAME_LOGIC,
      `Game error: ${reason}`,
      { reason }
    );
  }

  handleInvalidMove(position: string): ReversiError {
    return this.handleError(
      ReversiErrorType.INVALID_MOVE,
      `Invalid move at ${position}. You must outflank at least one opponent disc.`,
      { position }
    );
  }

  handleNotYourTurn(): ReversiError {
    return this.handleError(
      ReversiErrorType.NOT_YOUR_TURN,
      "It's not your turn. Please wait.",
      undefined
    );
  }

  handleConnectionError(error: any): ReversiError {
    return this.handleError(
      ReversiErrorType.CONNECTION_ERROR,
      'Connection lost. Attempting to reconnect...',
      error,
      true
    );
  }

  handleCorruptedData(dataType: string, error: any): ReversiError {
    return this.handleError(
      ReversiErrorType.CORRUPTED_DATA,
      'Unable to load saved game. Starting new game.',
      { dataType, error },
      true
    );
  }

  handleSaveError(gameId: string, error: any): ReversiError {
    return this.handleError(
      ReversiErrorType.SAVE_ERROR,
      'Failed to save game. Your progress may be lost.',
      { gameId, error },
      true
    );
  }

  handleLoadError(gameId: string, error: any): ReversiError {
    return this.handleError(
      ReversiErrorType.LOAD_ERROR,
      'Failed to load game. Starting new game.',
      { gameId, error },
      true
    );
  }

  // Internal helpers
  private isRecoverable(type: ReversiErrorType): boolean {
    switch (type) {
      case ReversiErrorType.INVALID_MOVE:
      case ReversiErrorType.NOT_YOUR_TURN:
      case ReversiErrorType.GAME_ENDED:
      case ReversiErrorType.GAME_LOGIC:
      case ReversiErrorType.CONNECTION_ERROR:
      case ReversiErrorType.CORRUPTED_DATA:
      case ReversiErrorType.AI_TIMEOUT:
      case ReversiErrorType.SAVE_ERROR:
      case ReversiErrorType.LOAD_ERROR:
        return true;
      default:
        return false;
    }
  }

  private logError(error: ReversiError): void {
    this.errorLog.push(error);
    if (this.errorLog.length > this.MAX_ERROR_LOG) {
      this.errorLog.shift();
    }
  }

  private showErrorNotification(error: ReversiError): void {
    const notificationType = error.recoverable
      ? NotificationType.WARNING
      : NotificationType.ERROR;
    this.notificationService.addNotification({
      type: notificationType,
      title: 'Reversi Error',
      message: error.message,
      duration: 5000,
    });
  }

  getErrorLog(): ReversiError[] {
    return [...this.errorLog];
  }

  clearErrorLog(): void {
    this.errorLog = [];
  }
}
